import { useRef, useEffect, memo } from 'react'

interface Props {
  story: Story
  onRead: (id: StoryId) => void
  onSave: (id: StoryId) => void
  onUnsave: (id: StoryId) => void
  registerForReadOnScroll?: (element: HTMLElement, id: StoryId) => void
  unregisterForReadOnScroll?: (element: HTMLElement) => void
}

const Story: React.FC<Props> = ({
  story,
  onRead,
  onSave,
  onUnsave,
  registerForReadOnScroll,
  unregisterForReadOnScroll,
}) => {
  const markerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (story.is_read || !registerForReadOnScroll || !unregisterForReadOnScroll) {
      return
    }
    const marker = markerRef.current
    if (!marker) {
      return
    }
    registerForReadOnScroll(marker, story.id)
    return () => unregisterForReadOnScroll(marker)
  }, [story.id, story.is_read, registerForReadOnScroll, unregisterForReadOnScroll])

  const handleClick = () => {
    if (!story.is_read) {
      onRead(story.id)
    }
  }

  const handleSaveToggle: React.MouseEventHandler = (e) => {
    e.stopPropagation()
    if (story.is_saved) {
      onUnsave(story.id)
    } else {
      onSave(story.id)
    }
  }

  return (
    <article
      data-story-id={story.id}
      onClick={handleClick}
      className={story.is_read ? '' : 'unread'}
    >
      <header>
        <div ref={markerRef} className="scroll-marker" aria-hidden="true" />
        <h2>
          <a href={story.uri} target="_blank" rel="noopener noreferrer">
            {story.title}
          </a>
        </h2>
      </header>
      <StoryContent sanitiziedHtml={story.sanitized_html_content} />
      <footer>
        <small>
          Published {new Date(story.publish_time * 1000).toISOString()} on {story.feed_title}
          {' '}
          <button
            onClick={handleSaveToggle}
            type="button"
            title={story.is_saved ? 'Unsave' : 'Save'}
          >
            {story.is_saved ? '★' : '☆'}
          </button>
        </small>
      </footer>
    </article>
  )
}

const StoryContent: React.FC<{ sanitiziedHtml: string }> = memo(({ sanitiziedHtml }) => {
  return (
    <div dangerouslySetInnerHTML={{__html: sanitiziedHtml }} />
  )
})

export default Story
